import { useCallback, useEffect } from 'react';
import { AlloyButton } from 'cat-ecommerce-alloy';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import { commonItemPropType } from '../../declarations.propTypes';
import { getOrder } from '@app/store/orders/actions';
import { addItemsToQuickOrder } from '@app/store/savedListDetail/actions';
import * as types from '@app/store/savedListDetail/constants';
import { ERROR_DOMAIN, ERROR_PATH } from '@app/constants/errorConstants';
import { useSystemFeedback } from '@app/hooks';
import { replaceTokensInString } from '@app/utils';
import { DEFAULT_SHOPPING_CART_MAX_ITEMS } from '@app/components/pages/checkout/shopping-cart/ShoppingCartPage/constants';
import { STATUS } from '@app/constants/commonConstants';
import { ORDER_GET_IDLE } from '@app/store/orders/constants';

const ImportSavedListLink = ({ item }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setError, clearError } = useSystemFeedback();
  const { setFieldValue, values } = useFormikContext();

  const { close, orderId, quantity = 0 } = item;
  const isDisabledButton = quantity < 1;

  const shoppingCartMaxItems =
    useSelector(s => s.dealer.shoppingCartMaxItems) ??
    DEFAULT_SHOPPING_CART_MAX_ITEMS;
  const orderCallStatus = useSelector(
    s => s.orders?.byId[orderId]?.orderCallStatus
  );
  const savedListCallStatus = useSelector(s => s.savedListDetailUI?.status);
  const ascendingOrderItems = useSelector(
    s => s.orders?.byId[orderId]?.ascendingOrderItems
  );

  const handleGetOrderError = useCallback(() => {
    dispatch({ type: ORDER_GET_IDLE, payload: orderId });
    dispatch({ type: types.SAVED_LIST_IMPORT_ITEM_FAIL });

    const networkErrorMessage = {
      message: t('IMPORT_SAVED_LIST_UNSUCCESFUL_ERROR')
    };
    setError(ERROR_DOMAIN.QUICK_ORDER, ERROR_PATH.MAIN, networkErrorMessage);
    close();
  }, [t, setError]);

  const handleGetOrderSuccess = useCallback(async () => {
    dispatch({ type: ORDER_GET_IDLE, payload: orderId });

    const cleanValues = values.items.filter(
      element => element.itemNumber !== '' && element.quantity !== ''
    );
    if (
      ascendingOrderItems.length + cleanValues.length <=
      shoppingCartMaxItems
    ) {
      await dispatch(
        addItemsToQuickOrder({
          cleanValues,
          setFieldValue,
          close,
          ascendingOrderItems
        })
      );
      dispatch({ type: types.SAVED_LIST_IMPORT_ITEM_SUCCESS });
    } else {
      const lineLimitErrorMessage = {
        message: replaceTokensInString(
          t('IMPORT_SAVED_LIST_ERROR_LINE_ITEM_LIMIT'),
          shoppingCartMaxItems
        )
      };
      setError(
        ERROR_DOMAIN.QUICK_ORDER,
        ERROR_PATH.MAIN,
        lineLimitErrorMessage
      );
      dispatch({ type: types.SAVED_LIST_IMPORT_ITEM_FAIL });
      close();
    }
  }, [
    orderId,
    values.items,
    ascendingOrderItems,
    shoppingCartMaxItems,
    close,
    dispatch,
    setFieldValue,
    setError,
    t
  ]);

  useEffect(() => {
    if (
      orderCallStatus === STATUS.RESOLVED &&
      savedListCallStatus === STATUS.PENDING
    ) {
      handleGetOrderSuccess();
    }
  }, [orderCallStatus, handleGetOrderSuccess]);

  useEffect(() => {
    if (orderCallStatus === STATUS.REJECTED) {
      handleGetOrderError();
    }
  }, [orderCallStatus, handleGetOrderError]);

  useEffect(() => {
    return () => clearError(ERROR_DOMAIN.CART, ERROR_PATH.SHOPPING_CART);
  }, [clearError]);

  const addToQuickOrder = () => {
    clearError(ERROR_DOMAIN.QUICK_ORDER, ERROR_PATH.MAIN);
    dispatch({ type: types.SAVED_LIST_IMPORT_ITEM_BEGIN });
    dispatch(getOrder(orderId));
  };

  return (
    <AlloyButton
      buttonType="primary"
      onClick={addToQuickOrder}
      disabled={isDisabledButton}
    >
      {t('SHP_CART_IMPORT')}
    </AlloyButton>
  );
};
export default ImportSavedListLink;

ImportSavedListLink.propTypes = {
  item: commonItemPropType
};
